/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {Grid, LinearProgress} from '@material-ui/core';
import classnames from 'classnames';
import {WorkBook} from 'xlsx/types';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {Entity, GenericObject} from '@ion/core/src/types/entities';
import {DropsheetOptions} from '@ion/core/src/types/templates';
import {process} from '@ion/core/src/xlsx/ExcelHelpers';
import {dropsheet} from '@ion/core/src/xlsx/importer';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {FileBrowseButton} from '@ion/dls/src/editors/controls/FileBrowseButton';
import {FileIcon} from '@ion/dls/src/icons/FileIcon';
import {NotificationGeneralAlertIcon} from '@ion/dls/src/icons/NotificationGeneralAlertIcon';
import {TrashCanIcon} from '@ion/dls/src/icons/TrashCanIcon';
import ImportProgress from '@ion/dls/src/imports/ImportProgress';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {formatBytes} from './DevicePasswordUtils';
import 'filepond/dist/filepond.min.css';

export interface BulkSCIUploadProps {
  classes: {
    [key: string]: string;
  };
  onTypeChange: Function;
  deviceTypes: any;
  setDeviceTypes: Function;
  errorDeviceTypes: GenericObject;
  deviceIds: Array<Number>;
  setDeviceIds: Function;
  isPersist?: boolean;
}

// defines the type definitions for import params ...
export type ImportParamsType = {
  fileName?: string;
  fileSize?: number;
  isComplete?: boolean;
  isFailure?: boolean,
  items?: Array<any>;
}

/**
 * Display Bulk SCI Import Dialog
 */
export const BulkSCIUpload = ({
  classes,
  onTypeChange,
  deviceTypes,
  setDeviceTypes,
  errorDeviceTypes,
  deviceIds,
  setDeviceIds,
  isPersist
}: BulkSCIUploadProps) => {
  const isMounted = useHasMounted();

  const [importStatus, setImportStatus]         = useState(false);
  const [importComplete, setImportComplete]     = useState<boolean>(false);
  const [importerOptions, setImporterOptions]   = useState<DropsheetOptions>();
  const [importParams, setImportParams]         = useState<ImportParamsType>();

  useEffect(() => {
    if (!isMounted) {
      initializeImporterOptions();
      initializeImportParams();
      setDeviceTypes({
        ...deviceTypes,
        idType : 'serialNumber'
      });
    } else {
      importerOptions.target = document.getElementById('bulksci-upload');
      dropsheet(importerOptions, setImportStatus);
    }
  }, [isMounted]);

  useEffect(() => {
    setImportComplete(importStatus && importParams?.isComplete);
  }, [importStatus, importParams?.isComplete]);

  const initializeImporterOptions = () => {
    setImporterOptions({
      onComplete  : onImportCompleted,
      onFailed    : onImportFailed,
      onSizeError : onImportFailed,
      parse       : true
    });
  };

  const initializeImportParams = function() {
    setImportParams({
      items      : [],
      isComplete : false,
      isFailure  : false
    });
  };

  const onImportCompleted = function(
    items: Array<any>,
    workbook: WorkBook,
    files: Array<any>
  ) {
    const file = files ? files[0] : {};
    const deviceIds = items?.map((each) => each.deviceIdentifier);
    if (!deviceIds.length) {
      return onImportFailed();
    };
    setDeviceIds(deviceIds);
    setImportParams({
      ...importParams,
      fileName   : file?.name,
      fileSize   : file?.size,
      isComplete : true,
      isFailure  : false,
      items
    });
  };

  const onImportFailed = function() {
    clearImport();
    setImportParams({
      ...importParams,
      isComplete : false,
      isFailure  : true
    });
  };

  const clearImport = function() {
    const importContainer = document.getElementById('device-import') as HTMLInputElement;
    if (importContainer) {
      importContainer.value = null;
    }
    setDeviceIds(null);
    setImportStatus(false);
    initializeImportParams();
  };

  function handleImport(files: FileList) {
    setImportStatus(true);
    process(files, importerOptions);
  };

  const getValue = useMemo(() => {
    if (deviceTypes?.deviceIds) {
      return deviceTypes?.deviceIds;
    } else if (deviceTypes?.deviceIdValues) {
      return deviceTypes?.deviceIdValues;
    } else return '';
  }, [deviceTypes]);

  const isBulkUploadDisabled = useMemo(() => (
    isPersist && deviceIds?.length
  ), [isPersist, deviceIds]);

  return (
    <>
      <div style={{display: 'flex'}}>
        <div className={classes.bulkContainer} id={'bulksci-upload'}>
          <h4 className={classes.uploadTitle}>{translate('admin-devices:view.bulkSciSetting.bulkUploadLabel')}</h4>
          <div className={isBulkUploadDisabled ? classes.disableUploadContainer : classes.uploadContainer}>
            <FileBrowseButton onImport={handleImport}
              entity={Entity.device}
              fileTypes='.csv, .xls, .xlsx' />
            <p className={classes.message}>
              {translate('common:editors.import.dragAndDropMsg')}
            </p>
          </div>
        </div>
        <div className={classes.orStyle}>{translate('admin-devices:view.bulkSciSetting.orLabel')}</div>
        <div className={classes.formTxtField}>
          <FormItem disableMinWidth customWidth={11}>
            <FormTextField required
              name={'deviceIdValues'}
              fieldLabel={translate('admin-devices:devicePassword.dialog.deviceIdentifier')}
              onChange={function(e) {
                onTypeChange(e);
              }}
              hasError={errorDeviceTypes?.typeValueError}
              errorText={translate('admin-devices:view.bulkSciSetting.identifierError')}
              value={deviceIds?.length ? deviceIds : deviceTypes?.deviceIdValues ? deviceTypes?.deviceIdValues : ''}
              multiline
              rows={8}
              disabled={!!(importComplete || deviceIds?.length)}
              InputProps={{
                classes : {
                  input : classes.input
                }
              }}/>
          </FormItem>
          {isBulkUploadDisabled &&
            <div>
              <TertiaryButton fontSize={'12px'} disabled={false} onClick={clearImport}>
                {translate('admin-devices:view.bulkSciSetting.reset')}
              </TertiaryButton>
            </div>}
        </div>
      </div>
      {importParams?.isFailure && (
        <div className={classes.results}>
          <div className={classes.icon}>
            <NotificationGeneralAlertIcon color={dlsColor('white')} />
          </div>
          {translate('common:editors.import.importErrorMsg')}
        </div>
      )}
      <div id='upload-status' className={classnames(importStatus
        ? classes.uploadStatusContainer : classes.hideUploadStatusContainer)}>
        <div className={classes.fileInfoSection}>
          <div className={classes.fileIcon}><FileIcon /></div>
          <div>
            <ImportProgress isCompleted={importComplete}
              text={importParams?.fileName || ''} />
          </div>
        </div>
        <div className={classes.fileSize}>
          <ImportProgress isCompleted={importComplete}
            text={formatBytes(importParams?.fileSize) || ''} />
        </div>
        <div className={classes.linearProgress}>
          <Grid xs item>
            {importStatus ? importParams.isComplete ? translate('common:editors.import.uploadCompleted')
              : translate('common:editors.import.uploadProgress') : ''}
          </Grid>
          <Grid xs item>
            {importStatus ? importParams.isComplete ? <LinearProgress variant='determinate' value={100} />
              : <LinearProgress /> : ''}
          </Grid>
        </div>
        <IconButton style={{padding: '0px'}} onClick={clearImport} icon={<TrashCanIcon />} />
      </div>
    </>
  );
};

export default BulkSCIUpload;
